import React, { useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

/**
 * A pared-down MyLab online order form.
 *
 * Deliberately not a faithful clone of Laboklin's full submission form: it
 * carries only the patient fields needed to ask a useful question, plus the
 * clinical presentation that the real form captures as free text. The point of
 * this screen is the moment before a sample is sent, not the form itself.
 */

const SPECIES_OPTIONS = [
  { char: 'H', label: 'Dog' },
  { char: 'K', label: 'Cat' },
  { char: 'P', label: 'Horse' },
];

const SEX_OPTIONS = [
  { value: 'm', label: 'Male' },
  { value: 'mk', label: 'Male neutered' },
  { value: 'w', label: 'Female' },
  { value: 'wk', label: 'Female neutered' },
];

interface SubmissionFormViewProps {
  onAskWhichTests: (summary: string) => void;
}

export const SubmissionFormView: React.FC<SubmissionFormViewProps> = ({ onAskWhichTests }) => {
  const navigate = useNavigate();

  const [patientName, setPatientName] = useState('');
  const [speciesChar, setSpeciesChar] = useState('H');
  const [breed, setBreed] = useState('');
  const [sex, setSex] = useState('wk');
  const [ageYears, setAgeYears] = useState('');
  const [presentation, setPresentation] = useState('');

  const speciesLabel = SPECIES_OPTIONS.find((s) => s.char === speciesChar)?.label ?? 'Dog';
  const sexLabel = SEX_OPTIONS.find((s) => s.value === sex)?.label ?? '';
  const canAsk = presentation.trim().length > 10;

  const handleAsk = () => {
    const descriptors = [
      patientName || 'Unnamed patient',
      breed ? `${breed} ${speciesLabel.toLowerCase()}` : speciesLabel.toLowerCase(),
      sexLabel.toLowerCase(),
      ageYears ? `${ageYears} years old` : '',
    ]
      .filter(Boolean)
      .join(', ');

    onAskWhichTests(
      `I am about to send a sample to Laboklin. Patient: ${descriptors}. ` +
        `Clinical presentation: ${presentation.trim()} ` +
        `Which tests should I order? Show me the options with prices.`
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h3" sx={{ mb: 2 }}>
        Online order
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <TextField
            fullWidth
            size="small"
            label="Patient name"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
          />
        </Box>
        <Box>
          <TextField
            select
            fullWidth
            size="small"
            label="Species"
            value={speciesChar}
            onChange={(e) => setSpeciesChar(e.target.value)}
          >
            {SPECIES_OPTIONS.map((s) => (
              <MenuItem key={s.char} value={s.char}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField
            fullWidth
            size="small"
            label="Breed"
            value={breed}
            onChange={(e) => setBreed(e.target.value)}
          />
        </Box>
        <Box>
          <TextField
            select
            fullWidth
            size="small"
            label="Gender"
            value={sex}
            onChange={(e) => setSex(e.target.value)}
          >
            {SEX_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <TextField
            fullWidth
            size="small"
            label="Age (years)"
            value={ageYears}
            onChange={(e) => setAgeYears(e.target.value)}
          />
        </Box>
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Clinical presentation"
        placeholder="e.g. PU/PD, weight loss, poor coat, drinking constantly for three weeks"
        value={presentation}
        onChange={(e) => setPresentation(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" color="primary" onClick={handleAsk} disabled={!canAsk}>
          Which tests should I order?
        </Button>
        <Button variant="outlined" onClick={() => navigate('/mylab')}>
          Back
        </Button>
      </Box>

      {!canAsk && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
          Describe the clinical presentation to get test suggestions.
        </Typography>
      )}
    </Paper>
  );
};

export default SubmissionFormView;
